'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser, useFirestore } from '@/firebase/client-provider';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  effectiveTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
  const { user } = useUser();
  const firestore = useFirestore();

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Set initial value
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load user's theme preference from Firestore (delayed to not block initial render)
  useEffect(() => {
    if (!user || !firestore) return;

    // Delay loading theme preference until after initial render
    const timer = setTimeout(() => {
      const loadThemePreference = async () => {
        try {
          const userDocRef = doc(firestore, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.themePreference && ['light', 'dark', 'system'].includes(data.themePreference)) {
              setThemeModeState(data.themePreference as ThemeMode);
            }
          }
        } catch (error) {
          console.error('Error loading theme preference:', error);
        }
      };

      loadThemePreference();
    }, 100); // Small delay to not block initial page render

    return () => clearTimeout(timer);
  }, [user, firestore]);

  // Save theme preference to Firestore
  const setThemeMode = async (mode: ThemeMode) => {
    setThemeModeState(mode);

    if (user && firestore) {
      try {
        const userDocRef = doc(firestore, 'users', user.uid);
        await setDoc(userDocRef, { themePreference: mode }, { merge: true });
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
    }
  };

  // Determine effective theme
  const effectiveTheme: 'light' | 'dark' = 
    themeMode === 'system' ? systemTheme : themeMode;

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(effectiveTheme);
  }, [effectiveTheme]);

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, effectiveTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
